import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import TextField from '../components/ui/TextField';
import Button from '../components/ui/Button';

// MUI Icons
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(0, 118, 255, 0.1) 0%, rgba(255, 255, 255, 0.1) 100%);
  background-image: url('https://images.unsplash.com/photo-1579548122080-c35fd6820ecb?q=80&w=2070&auto=format&fit=crop');
  background-size: cover;
  background-position: center;
  background-blend-mode: overlay;
  padding: var(--space-md);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
  }
`;

const LoginCard = styled.div`
  position: relative;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 32px rgba(0, 118, 255, 0.1);
  border-radius: var(--radius-xl);
  width: 100%;
  max-width: 420px;
  padding: var(--space-xl);
  animation: ${fadeIn} 0.6s ease-out;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    transition: transform 0.3s ease;
  }
  
  @media (max-width: 768px) {
    padding: var(--space-lg);
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: var(--space-xl);
  animation: ${fadeIn} 0.8s ease-out;
  
  svg {
    font-size: 56px;
    color: var(--primary-color);
    margin-bottom: var(--space-sm);
    animation: ${pulse} 2s infinite ease-in-out;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: var(--font-xxl);
  margin: 0;
  text-align: center;
  background: linear-gradient(135deg, var(--primary-color) 0%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: var(--font-md);
  margin-top: var(--space-xs);
  margin-bottom: var(--space-lg);
  text-align: center;
  animation: ${fadeIn} 1s ease-out;
`;

const DemoNote = styled.div`
  margin-top: var(--space-sm);
  margin-bottom: var(--space-md);
  padding: 10px 12px;
  border-radius: var(--radius-md);
  background: #f1f5ff;
  color: #1e3a8a;
  font-size: var(--font-sm);
  text-align: center;
  border: 1px dashed #93c5fd;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  
  & > * {
    animation: ${slideIn} 0.6s ease-out forwards;
    opacity: 0;
    
    &:nth-child(1) { animation-delay: 0.2s; }
    &:nth-child(2) { animation-delay: 0.4s; }
    &:nth-child(3) { animation-delay: 0.6s; }
  }
`;

const ErrorMessage = styled.div`
  color: var(--danger-color);
  background-color: var(--danger-light);
  padding: var(--space-sm);
  border-radius: var(--radius-md);
  margin-bottom: var(--space-md);
  font-size: var(--font-sm);
  animation: ${fadeIn} 0.3s ease-out;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  
  svg {
    font-size: 20px;
  }
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  margin-top: var(--space-lg);
`;

const StoreIcon = styled(StorefrontIcon)`
  font-size: 32px !important;
  color: var(--primary-color);
  margin-right: var(--space-sm);
`;

const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-md);
`;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { auth, login, clearError } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    document.title = 'Sign In - POS System';
  }, []);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      await login({ username, password });
      navigate('/dashboard');
    } catch (error) {
      console.error('Login failed:', error);
      setIsSubmitting(false);
    }
  };
  
  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <BrandContainer>
            <StoreIcon />
            <ShoppingCartIcon />
          </BrandContainer>
          <Title>POS System</Title>
          <Subtitle>Welcome back! Please sign in to continue</Subtitle>
          <DemoNote>
            Demo credentials — Username: <strong>admin</strong>, Password: <strong>admin123</strong>
          </DemoNote>
        </LogoContainer>
        
        {auth.error && (
          <ErrorMessage>
            <LockIcon />
            {auth.error}
          </ErrorMessage>
        )}
        
        <Form onSubmit={handleSubmit}>
          <TextField
            label="Username"
            type="text"
            placeholder="Enter your username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              clearError();
            }}
            fullWidth
            required
            startIcon={<PersonIcon />}
          />
          
          <TextField
            label="Password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              clearError();
            }}
            fullWidth
            required
            startIcon={<LockIcon />}
          />
          
          <FormActions>
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth 
              loading={isSubmitting}
              disabled={!username || !password}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>
          </FormActions>
        </Form>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login; 